import { showWait } from '@zyno-io/vue-foundation';

import { Logger } from '../services/logger';
import { isChunkLoadError, recordChunkLoadFailure } from './chunk-load-error';
import { UpdateChecker } from './update.helpers';

const logger = Logger.createScoped('ChunkLoad');

let pendingCheck: Promise<boolean> | null = null;

/**
 * Recover from lazy chunks that fail to load outside `wrapRuntimeImport` — modal imports in click
 * handlers, and third-party lazy loads such as uxrr's recorder/OTel chunks, which reject in the
 * background. Vite's preload helper fires `vite:preloadError` for every dynamic import in the build;
 * `unhandledrejection` covers anything that bypasses it.
 *
 * A failed chunk alone doesn't prove a deploy (it may be a network blip), so confirm against
 * /version.json first. Once confirmed, UpdateChecker's update flag arms the idle auto-reload
 * (useIdleUpdateReload) and the sidebar's update button. When the failure follows a recent user
 * gesture, reload straight away if nothing blocks it (a call, an open modal). Because
 * reloading needs a confirmed newer version, the reloaded tab can't loop.
 *
 * Doesn't preventDefault: the caller still sees the rejection.
 */
export function installChunkLoadRecovery() {
    window.addEventListener('vite:preloadError', e => handleChunkLoadFailure((e as Event & { payload?: unknown }).payload));
    window.addEventListener('unhandledrejection', e => handleChunkLoadFailure(e.reason));
}

function handleChunkLoadFailure(err: unknown) {
    if (!isChunkLoadError(err)) return;
    recordChunkLoadFailure();
    // Read synchronously: transient activation lapses while the version check is in flight.
    const isUserInitiated = navigator.userActivation?.isActive ?? false;

    pendingCheck ??= UpdateChecker.checkNow().finally(() => (pendingCheck = null));
    void pendingCheck.then(isUpdateAvailable => {
        if (!isUpdateAvailable) return;
        if (!isUserInitiated || !UpdateChecker.canReloadNow()) return;
        logger.info('Reloading ZynoSuite after a chunk failed to load');
        showWait('Updating ZynoSuite...');
        window.location.reload();
    });
}
